import { NotificationSuccessIcon } from '@tableau/icons/NotificationSuccessIcon';
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  CheckBoxWidget,
  CheckBoxWithLabelWidget,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  InlineAlertType,
  InlineAlertWidget,
  ProgressBarWidget,
} from '@tableau/widgets';
import React from 'react';

import { CreateCustomDialogFn } from '~commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { classes } from '~src/style';

import { toPlanContentType } from '../details/planContentsService';
import { typeLabel } from '../details/planContentTypeLabel';
import { IImportItem, IImportManifestItem } from '../serverApi/types/IBundleImport';
import { canSelectImportItems, IImportPreviewData, importItemKey, isOverwrite } from '../utils/bundleImportActions';
import {
  BundleImportController,
  BundleImportState,
  IImportManifestData,
  ImportStartError,
  INITIAL_BUNDLE_IMPORT_STATE,
  neverStarted,
} from '../utils/BundleImportController';
import { ImportBundleDialogStyles as Styles } from './ImportBundleDialogStyles';

const TEST_ID = 'content-relay-import';

export const ImportBundleDialogTestIds = {
  Dialog: TEST_ID,
  Item: `${TEST_ID}-item-`,
  SelectAll: `${TEST_ID}-select-all`,
  Summary: `${TEST_ID}-selected-count`,
  WholePackageNotice: `${TEST_ID}-whole-package`,
  OverwriteWarning: `${TEST_ID}-overwrite-warning`,
  ConfirmOverwrite: `${TEST_ID}-confirm-overwrite`,
  StartError: `${TEST_ID}-start-error`,
  LoadError: `${TEST_ID}-load-error`,
  DeadEnd: `${TEST_ID}-dead-end`,
  Progress: `${TEST_ID}-progress`,
  Result: `${TEST_ID}-result`,
  ManifestSummary: `${TEST_ID}-manifest-summary`,
  ManifestError: `${TEST_ID}-manifest-error`,
  ToggleDetails: `${TEST_ID}-toggle-details`,
  ManifestItem: `${TEST_ID}-manifest-item-`,
  ShowMore: `${TEST_ID}-show-more`,
  Cancel: `${TEST_ID}-cancel`,
  TryAgain: `${TEST_ID}-try-again`,
  Import: `${TEST_ID}-import`,
  CancelImport: `${TEST_ID}-cancel-import`,
  Close: `${TEST_ID}-close`,
};

export interface ImportBundleDialogProps {
  /** The uploaded bundle to import. */
  bundleLuid: string;
  onClose: () => void;
}

/** "<type> · <status>", or just the status for content types this UI doesn't name yet. */
function itemDetail(contentType: string, status: string): string {
  const planType = toPlanContentType(contentType);
  return planType ? Messages.relayImportItemDetail({ TYPE: typeLabel(planType), STATUS: status }) : status;
}

function showCancelErrorToast(): void {
  ToasterService.instance.pop({ type: ToastType.Error, text: Messages.relayImportCancelErrorToast() });
}

/**
 * Destination-side import of an uploaded content relay bundle (W-24368277). Previews what the import
 * writes to this site, with every item selected by default and items that overwrite existing content
 * flagged; nothing that overwrites is imported until the user confirms it. The import then runs as a
 * background job: its progress is polled (it can be canceled, or the dialog closed while it keeps
 * running), and when it ends the manifest shows a summary with per-item details on demand.
 *
 * The preview / trigger / poll / manifest logic lives in {@link BundleImportController}; this component
 * owns the selection and confirmation, and renders the controller's state.
 */
export function ImportBundleDialog(props: ImportBundleDialogProps): JSX.Element {
  const [importState, setImportState] = React.useState<BundleImportState>(INITIAL_BUNDLE_IMPORT_STATE);
  const [unselectedKeys, setUnselectedKeys] = React.useState<{ [key: string]: boolean }>({});
  // The overwriting items the user confirmed while they were selected. Deselecting an item forgets its
  // confirmation, so selecting any overwriting item again asks again; a new preview forgets them all.
  const [confirmedKeys, setConfirmedKeys] = React.useState<{ [key: string]: boolean }>({});
  const [showDetails, setShowDetails] = React.useState(false);

  const controllerRef = React.useRef<BundleImportController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new BundleImportController(props.bundleLuid, setImportState, showCancelErrorToast);
  }
  const controller = controllerRef.current;

  React.useEffect(function() {
    controller.load();
    return function() { controller.dispose(); };
  }, [controller]);

  const preview = importState.preview;
  // A confirmation covers what the user saw: when the preview is read again (e.g. the server asked for a
  // confirmation the old one didn't need), the user confirms the new one.
  React.useEffect(function() {
    setConfirmedKeys({});
  }, [preview]);
  const items: ReadonlyArray<IImportItem> = preview ? preview.items : [];
  const selectable = !!preview && canSelectImportItems(preview);
  const keys = items.map(importItemKey);
  const selectedKeys = selectable ? keys.filter(function(key) { return !unselectedKeys[key]; }) : keys;
  const overwriteKeys = selectedKeys.filter(function(key) { return isOverwrite(items[keys.indexOf(key)]); });
  // A whole-bundle import overwrites everything the service counted, listed or not. When only part of
  // the bundle is listed and the service sent no count, assume something may be overwritten. Without
  // summary counts the server only imports with confirmOverwrite, so the user confirms whatever the
  // listed items say.
  const overwriteCount = selectable ? overwriteKeys.length : (preview ? preview.overwrittenCount : 0);
  const countUnknown = !selectable && !!preview && !preview.overwrittenCountExact;
  // Also when the server rejected an unconfirmed start: it found something to overwrite.
  const overwriteUnknown = !!preview && items.length > 0
    && (countUnknown || preview.summaryUnavailable || importState.startError === 'confirmRequired');
  const needsConfirm = overwriteCount > 0 || overwriteUnknown;
  const overwriteConfirmed = (!overwriteUnknown || !!confirmedKeys['*']) && (selectable
    ? overwriteKeys.every(function(key) { return confirmedKeys[key]; })
    : !!confirmedKeys['*']);

  const toggleConfirm = function(checked: boolean): void {
    const next: { [key: string]: boolean } = {};
    if (checked) {
      next['*'] = true;
      overwriteKeys.forEach(function(key) { next[key] = true; });
    }
    setConfirmedKeys(next);
  };

  const onImport = function(): void {
    const allSelected = selectedKeys.length === keys.length;
    const selectedItemIds = selectable && !allSelected
      ? selectedKeys.map(function(key) { return items[keys.indexOf(key)].itemId; })
      : undefined;
    controller.start(selectedItemIds, needsConfirm && overwriteConfirmed);
  };

  const canImport = importState.phase === 'select' && items.length > 0 && selectedKeys.length > 0
    && (!needsConfirm || overwriteConfirmed);

  const selection: ISelectionProps = {
    preview: preview,
    selectable: selectable,
    keys: keys,
    selectedKeys: selectedKeys,
    needsConfirm: needsConfirm,
    // 0 shows the "may already exist" warning instead of a count.
    overwriteWarningCount: countUnknown ? 0 : overwriteCount,
    overwriteConfirmed: overwriteConfirmed,
    disabled: importState.phase !== 'select',
    startError: importState.startError,
    onToggleItem: function(key: string) {
      const next = { ...unselectedKeys };
      next[key] = !next[key];
      setUnselectedKeys(next);
      if (next[key] && confirmedKeys[key]) {
        const confirmed = { ...confirmedKeys };
        delete confirmed[key];
        setConfirmedKeys(confirmed);
      }
    },
    onToggleAll: function() {
      const next: { [key: string]: boolean } = {};
      if (selectedKeys.length === keys.length) {
        keys.forEach(function(key) { next[key] = true; });
        setConfirmedKeys({});
      }
      setUnselectedKeys(next);
    },
    onToggleConfirm: toggleConfirm,
  };

  return (
    <DialogWidget
      type={DialogType.NormalWithTitleBar}
      title={Messages.relayImportTitle()}
      showCloseButton={true}
      onCloseRequest={props.onClose}
      positioningBehavior={DialogPositioningBehavior.Centered}
      shouldRecenterOnUpdate={true}
      isModal={true}
      dialogWidth={560}
      footerContent={renderFooter(importState, {
        canImport: canImport,
        onImport: onImport,
        onCancelImport: function() { controller.cancel(); },
        onTryAgain: function() { controller.load(); },
        onClose: props.onClose,
      })}
      testId={TEST_ID}
    >
      {renderBody(importState, selection, {
        showDetails: showDetails,
        onToggleDetails: function() { setShowDetails(!showDetails); },
        onLoadMore: function() { controller.loadMoreManifest(); },
      })}
    </DialogWidget>
  );
}

interface ISelectionProps {
  preview: IImportPreviewData | undefined;
  selectable: boolean;
  keys: ReadonlyArray<string>;
  selectedKeys: ReadonlyArray<string>;
  /** Something selected overwrites, or may (unknown count): the import needs the user's confirmation. */
  needsConfirm: boolean;
  /** How many selected items overwrite; 0 when that's unknown or none is known. */
  overwriteWarningCount: number;
  overwriteConfirmed: boolean;
  disabled: boolean;
  startError: ImportStartError | undefined;
  onToggleItem: (key: string) => void;
  onToggleAll: () => void;
  onToggleConfirm: (checked: boolean) => void;
}

interface IManifestProps {
  showDetails: boolean;
  onToggleDetails: () => void;
  onLoadMore: () => void;
}

function renderSpinner(phase: string, message: string): JSX.Element {
  return (
    <div className={Styles.StatusState} data-tb-test-id={`${TEST_ID}-${phase}`}>
      <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} alt={message} />
      <div className={Styles.StatusMessage}>{message}</div>
    </div>
  );
}

function renderBody(state: BundleImportState, selection: ISelectionProps, manifest: IManifestProps): JSX.Element {
  switch (state.phase) {
    case 'loading':
      return renderSpinner(state.phase, Messages.relayImportLoadingMessage());

    case 'loadError':
      return (
        <InlineAlertWidget type={InlineAlertType.Error} testId={ImportBundleDialogTestIds.LoadError}>
          {Messages.relayImportLoadError()}
        </InlineAlertWidget>
      );

    case 'alreadyImported':
    case 'unavailable':
      return (
        <InlineAlertWidget
          type={state.phase === 'alreadyImported' ? InlineAlertType.Info : InlineAlertType.Error}
          testId={`${ImportBundleDialogTestIds.DeadEnd}-${state.phase}`}
        >
          {state.phase === 'alreadyImported' ? Messages.relayImportAlreadyImported() : Messages.relayImportUnavailable()}
        </InlineAlertWidget>
      );

    case 'tracking':
      return renderProgress(state);

    case 'cancelling':
      return renderSpinner(state.phase, Messages.relayImportCancelling());

    case 'succeeded':
    case 'failed':
    case 'cancelled':
    case 'statusUnavailable':
      return renderResult(state, manifest);

    default:
      // select / starting
      return renderSelect(selection);
  }
}

function renderSelect(props: ISelectionProps): JSX.Element {
  const preview = props.preview;
  if (!preview || !preview.items.length) {
    return <div className={Styles.Intro}>{Messages.relayImportEmpty()}</div>;
  }
  const allSelected = props.selectedKeys.length === props.keys.length;
  return (
    <div>
      <div className={Styles.Intro}>{Messages.relayImportIntro()}</div>
      {props.startError && (
        <InlineAlertWidget
          type={props.startError === 'confirmRequired' ? InlineAlertType.Warning : InlineAlertType.Error}
          className={Styles.Alert}
          testId={ImportBundleDialogTestIds.StartError}
        >
          {props.startError === 'confirmRequired' ? Messages.relayImportConfirmRequired() : Messages.relayImportStartError()}
        </InlineAlertWidget>
      )}
      {!props.selectable && (
        <InlineAlertWidget type={InlineAlertType.Info} className={Styles.Alert} testId={ImportBundleDialogTestIds.WholePackageNotice}>
          {preview.complete
            ? Messages.relayImportWholePackage()
            : Messages.relayImportPartialList({ SHOWN: String(preview.items.length), TOTAL: String(preview.totalCount) })}
        </InlineAlertWidget>
      )}
      {props.selectable && (
        <div className={Styles.SelectAll}>
          <CheckBoxWithLabelWidget
            checked={allSelected}
            indeterminate={props.selectedKeys.length > 0 && !allSelected}
            disabled={props.disabled}
            handleChange={props.onToggleAll}
            label={Messages.relayPlanContentsSelectAll()}
            testId={ImportBundleDialogTestIds.SelectAll}
          />
        </div>
      )}
      <div className={Styles.ItemList}>
        {preview.items.map(function(item, index) { return renderPreviewItem(item, props.keys[index], props); })}
      </div>
      {props.selectable && (
        <div className={Styles.Summary} data-tb-test-id={ImportBundleDialogTestIds.Summary}>
          {Messages.relayImportSelectedCount({ SELECTED: String(props.selectedKeys.length), TOTAL: props.keys.length })}
        </div>
      )}
      {props.needsConfirm && (
        <div className={Styles.Confirm}>
          <InlineAlertWidget type={InlineAlertType.Warning} className={Styles.Alert} testId={ImportBundleDialogTestIds.OverwriteWarning}>
            {props.overwriteWarningCount > 0
              ? Messages.relayImportOverwriteWarning({ COUNT: props.overwriteWarningCount })
              : Messages.relayImportOverwriteWarningUnknown()}
          </InlineAlertWidget>
          <CheckBoxWithLabelWidget
            checked={props.overwriteConfirmed}
            disabled={props.disabled}
            handleChange={props.onToggleConfirm}
            label={Messages.relayImportConfirmOverwrite()}
            testId={ImportBundleDialogTestIds.ConfirmOverwrite}
          />
        </div>
      )}
    </div>
  );
}

function renderPreviewItem(item: IImportItem, key: string, props: ISelectionProps): JSX.Element {
  const overwrite = isOverwrite(item);
  const name = item.name || '';
  return (
    <div key={key} className={Styles.ItemRow} data-tb-test-id={ImportBundleDialogTestIds.Item + key}>
      {props.selectable && (
        <CheckBoxWidget
          checked={props.selectedKeys.indexOf(key) !== -1}
          disabled={props.disabled}
          handleChange={function() { props.onToggleItem(key); }}
          removeLeftMargin={true}
          aria-label={Messages.relayPlanContentsSelectItem({ NAME: name })}
          testId={ImportBundleDialogTestIds.Item + key + '-select'}
        />
      )}
      <div className={Styles.ItemText}>
        <div className={Styles.ItemName} title={name}>{name}</div>
        <div className={classes(Styles.ItemDetail, overwrite && Styles.ItemDetailWarning)}>
          {itemDetail(item.contentType, overwrite ? Messages.relayImportItemReplaces() : Messages.relayImportItemNew())}
        </div>
      </div>
    </div>
  );
}

// Indeterminate until the service reports a percentage.
function renderProgress(state: BundleImportState): JSX.Element {
  const execution = state.execution;
  const running = !!execution && (execution.state || '').toUpperCase() === 'RUNNING';
  return (
    <div className={Styles.StatusState}>
      <ProgressBarWidget
        className={Styles.ProgressBar}
        label={running ? Messages.relayImportRunning() : Messages.relayImportQueued()}
        value={running && execution.progress > 0 ? execution.progress : undefined}
        max={100}
        message={Messages.relayImportBackgroundNote()}
        testId={ImportBundleDialogTestIds.Progress}
      />
    </div>
  );
}

function renderResult(state: BundleImportState, props: IManifestProps): JSX.Element {
  let headline: JSX.Element;
  if (state.phase === 'succeeded') {
    headline = (
      <div className={Styles.StatusState}>
        <NotificationSuccessIcon size={24} />
        <div className={Styles.StatusMessage}>{Messages.relayImportSucceeded()}</div>
      </div>
    );
  } else {
    // An import that never reached the service wrote nothing (and has no manifest).
    const notStarted = neverStarted(state.execution);
    const alert = state.phase === 'failed'
      ? { type: InlineAlertType.Error, text: notStarted ? Messages.relayImportNotStartedFailed() : Messages.relayImportFailed() }
      : state.phase === 'cancelled'
        ? { type: InlineAlertType.Info, text: notStarted ? Messages.relayImportNotStartedCancelled() : Messages.relayImportCancelled() }
        : { type: InlineAlertType.Warning, text: Messages.relayImportStatusUnavailable() };
    headline = <InlineAlertWidget type={alert.type}>{alert.text}</InlineAlertWidget>;
  }
  return (
    <div data-tb-test-id={`${ImportBundleDialogTestIds.Result}-${state.phase}`}>
      {headline}
      {renderManifest(state, props)}
    </div>
  );
}

function renderManifest(state: BundleImportState, props: IManifestProps): JSX.Element {
  if (state.manifestStatus === 'none') {
    return null;
  }
  const manifest = state.manifest;
  if (!manifest) {
    return state.manifestStatus === 'loading'
      ? <div className={Styles.ManifestActions}><ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} /></div>
      : renderManifestError(props);
  }
  return (
    <div>
      <div className={Styles.ManifestSummary} data-tb-test-id={ImportBundleDialogTestIds.ManifestSummary}>
        {Messages.relayImportSummary({
          CREATED: String(manifest.createdCount),
          OVERWRITTEN: String(manifest.overwrittenCount),
          FAILED: String(manifest.failedCount),
        })}
      </div>
      {manifest.items.length > 0 && (
        <div className={Styles.ManifestActions}>
          <ButtonWidget buttonType={ButtonType.LowEmphasis} onClick={props.onToggleDetails} testId={ImportBundleDialogTestIds.ToggleDetails}>
            {props.showDetails ? Messages.relayImportHideDetails() : Messages.relayImportShowDetails()}
          </ButtonWidget>
        </div>
      )}
      {props.showDetails && renderManifestDetails(state, manifest, props)}
    </div>
  );
}

function renderManifestDetails(state: BundleImportState, manifest: IImportManifestData, props: IManifestProps): JSX.Element {
  return (
    <div className={Styles.Details}>
      <div className={Styles.ItemList}>
        {manifest.items.map(function(item, index) { return renderManifestItem(item, index); })}
      </div>
      {state.manifestStatus === 'error' && renderManifestError(props)}
      {state.manifestStatus !== 'error' && manifest.nextPageToken && (
        <div className={Styles.ManifestActions}>
          <ButtonWidget
            buttonType={ButtonType.LowEmphasis}
            onClick={props.onLoadMore}
            disabled={state.manifestStatus === 'loading'}
            showSpinner={state.manifestStatus === 'loading'}
            testId={ImportBundleDialogTestIds.ShowMore}
          >
            {Messages.relayPlanContentsShowMore()}
          </ButtonWidget>
        </div>
      )}
    </div>
  );
}

function renderManifestItem(item: IImportManifestItem, index: number): JSX.Element {
  const failed = item.succeeded === false;
  const status = failed
    ? Messages.relayImportItemFailed()
    : isOverwrite(item) ? Messages.relayImportItemOverwritten() : Messages.relayImportItemCreated();
  const key = importItemKey(item, index);
  return (
    <div key={key} className={Styles.ItemRow} data-tb-test-id={ImportBundleDialogTestIds.ManifestItem + key}>
      <div className={Styles.ItemText}>
        <div className={Styles.ItemName} title={item.name}>{item.name}</div>
        <div className={classes(Styles.ItemDetail, failed && Styles.ItemDetailError)}>{itemDetail(item.contentType, status)}</div>
      </div>
    </div>
  );
}

function renderManifestError(props: IManifestProps): JSX.Element {
  return (
    <div className={Styles.ManifestSummary} data-tb-test-id={ImportBundleDialogTestIds.ManifestError}>
      <InlineAlertWidget type={InlineAlertType.Error}>{Messages.relayImportManifestLoadError()}</InlineAlertWidget>
      <div className={Styles.ManifestActions}>
        <ButtonWidget buttonType={ButtonType.LowEmphasis} onClick={props.onLoadMore}>{Messages.tryAgain()}</ButtonWidget>
      </div>
    </div>
  );
}

interface IFooterProps {
  canImport: boolean;
  onImport: () => void;
  onCancelImport: () => void;
  onTryAgain: () => void;
  onClose: () => void;
}

function renderFooter(state: BundleImportState, props: IFooterProps): JSX.Element {
  switch (state.phase) {
    case 'loadError':
      return (
        <DialogFooterButtonsWidget
          buttons={[
            { buttonType: ButtonType.Outline, onClick: props.onClose, children: Messages.close(), testId: ImportBundleDialogTestIds.Close },
            { buttonType: ButtonType.Primary, onClick: props.onTryAgain, children: Messages.tryAgain(), testId: ImportBundleDialogTestIds.TryAgain },
          ]}
        />
      );

    case 'tracking':
      // Close leaves the import running server-side; Cancel import stops it.
      return (
        <DialogFooterButtonsWidget
          buttons={[
            { buttonType: ButtonType.Outline, onClick: props.onCancelImport, children: Messages.relayImportCancel(), testId: ImportBundleDialogTestIds.CancelImport },
            { buttonType: ButtonType.Primary, onClick: props.onClose, children: Messages.close(), testId: ImportBundleDialogTestIds.Close },
          ]}
        />
      );

    case 'cancelling':
    case 'alreadyImported':
    case 'unavailable':
      return (
        <DialogFooterButtonsWidget
          buttons={[{ buttonType: ButtonType.Outline, onClick: props.onClose, children: Messages.close(), testId: ImportBundleDialogTestIds.Close }]}
        />
      );

    case 'succeeded':
    case 'failed':
    case 'cancelled':
    case 'statusUnavailable':
      return (
        <DialogFooterButtonsWidget
          buttons={[{ buttonType: ButtonType.Primary, onClick: props.onClose, children: Messages.done(), testId: ImportBundleDialogTestIds.Close }]}
        />
      );

    default:
      // loading / select / starting
      return (
        <DialogFooterButtonsWidget
          buttons={[
            { buttonType: ButtonType.Outline, onClick: props.onClose, children: Messages.cancel(), testId: ImportBundleDialogTestIds.Cancel },
            {
              buttonType: ButtonType.Primary,
              onClick: props.onImport,
              disabled: !props.canImport,
              children: Messages.relayImportAction(),
              testId: ImportBundleDialogTestIds.Import,
            },
          ]}
        />
      );
  }
}

/** Options for ReactModalService.openCustom. Opened from the upload dialog once the bundle is ready. */
export function ImportBundleDialogOpts(bundleLuid: string): CreateCustomDialogFn {
  return function(closeAndDismiss) {
    return <ImportBundleDialog bundleLuid={bundleLuid} onClose={closeAndDismiss.close} />;
  };
}
